/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import './wizardSteps.tpl.html';

/*
 * This directive displays a set of dots connected by a horizontal line representing the progress along a series of
 * steps in a wizard. Below each dot is displayed the title of the step. Completed, current, and uncompleted steps are
 * highlighted distinctly.
 */

/**
 * Defines the sbsf-wizard-steps directive.
 *
 * @returns {Object} - The directive settings
 */
export function directive() {
  return {
    controller: 'WizardStepsCtrl',
    templateUrl: 'shared/wizard/wizardSteps.tpl.html',
    restrict: 'E',
    scope: {
      wizard: '='
    }
  };
}

controller.$inject = ['$scope', 'WizardService'];

/**
 * The controller for the directive.
 */
export function controller($scope, WizardService) {
  // Publish functions in the scope
  $scope.getDotClass = getDotClass;
  $scope.getLeftLineClass = getLeftLineClass;
  $scope.getNameClass = getNameClass;
  $scope.getRightLineClass = getRightLineClass;
  $scope.getStepKeys = getStepKeys;
  $scope.goToStep = goToStep;

  const CLASS_COMPLETED = 'sbsf-completed';
  const CLASS_CURRENT = 'sbsf-current';
  const CLASS_HIDDEN = 'sbsf-hidden';
  const CLASS_UNKNOWN = 'sbsf-unknown';

  /**
   * Returns the CSS class to be added to the specified step's dot to indicate completed, current, or future.
   *
   * @param {number} step - The step index to be checked.
   * @returns {string} the CSS class to be added
   */
  function getDotClass(step) {
    if (WizardService.isCurrentStep(step)) {
      return CLASS_CURRENT;
    }

    if (WizardService.isCompletedStep(step)) {
      return CLASS_COMPLETED;
    }

    // Use the default styling
    return undefined;
  }

  /**
   * Returns the CSS class to be added to the line on the left side of the specified step's dot to indicate completed,
   * current, or future.
   *
   * @param {number} step - The step index to be checked.
   * @returns {string} the CSS class to be added
   */
  function getLeftLineClass(step) {
    // For the first step we don't show a line on the left side of the dot.
    if (WizardService.isFirstStep(step)) {
      return CLASS_HIDDEN;
    }

    // If the step is an unknown future step we show a dotted line on the left
    if (WizardService.isUnknownStep(step)) {
      return CLASS_UNKNOWN;
    }

    // For completed steps and the current step the left side line shows as completed.
    if (WizardService.isCompletedStep(step) || WizardService.isCurrentStep(step)) {
      return CLASS_COMPLETED;
    }

    // Use the default styling
    return undefined;
  }

  /**
   * Returns the CSS class to be added to the specified step's name to indicate completed, current, or future.
   *
   * @param {number} step - The step index to be checked.
   * @returns {string} the CSS class to be added
   */
  function getNameClass(step) {
    if (WizardService.isCurrentStep(step)) {
      return CLASS_CURRENT;
    }

    if (WizardService.isCompletedStep(step)) {
      return CLASS_COMPLETED;
    }

    // Use the default styling
    return undefined;
  }

  /**
   * Returns the CSS class to be added to the line on the right side of the specified step's dot to indicate
   * completed, current, or future.
   *
   * @param {number} step - The step index to be checked.
   * @returns {string} the CSS class to be added
   */
  function getRightLineClass(step) {
    // For the last step we don't show a line on the right side of the dot.
    if (WizardService.isLastStep(step)) {
      return CLASS_HIDDEN;
    }

    if (WizardService.isCompletedStep(step)) {
      return CLASS_COMPLETED;
    }

    // Use the default styling
    return undefined;
  }

  /**
   * Returns the I18N keys of the names of the steps.
   *
   * @return {string[]} the I18N keys of the names of the steps.
   */
  function getStepKeys() {
    return WizardService.getStepKeys();
  }

  /**
   * Moves to a specific step in the wizard
   *
   * @param {number} step - The index of the page to go back to.
   *
   * @returns {Promise} a promise that is resolved when there is nothing to confirm or when the user confirms. The
   * promise is rejected if the user decides to stay.
   */
  function goToStep(step) {
    // You can only go to a completed step
    if (WizardService.isCompletedStep(step)) {
      return $scope.wizard.goToStep(step);
    }
  }
}
